import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FaEdit, FaTrash, FaMotorcycle, FaCar, FaPlus, FaFileAlt } from 'react-icons/fa';
import VehicleForm from './VehicleForm';
import VehicleDocuments from './VehicleDocuments';
import Table from '../common/Table';
import IconButton from '../common/IconButton';
import Button from '../common/Button';
import useApi from '../../hooks/useApi';
import { toast } from 'react-toastify';

/**
 * List of user vehicles
 * @param {Object} props
 * @param {Array} props.vehicles - List of vehicles
 * @param {Function} props.setVehicles - Update vehicles state
 */
function VehicleList({ vehicles, setVehicles }) {
  const [editingVehicle, setEditingVehicle] = useState(null); // For edit mode
  const [isAdding, setIsAdding] = useState(false); // For add mode
  const [documentVehicle, setDocumentVehicle] = useState(null);
  const { delete: deleteVehicle } = useApi();

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this vehicle from your community profile?')) return;
    try {
      await deleteVehicle(`/api/vehicles/${id}`);
      setVehicles((current) => current.filter((vehicle) => vehicle.id !== id));
      setDocumentVehicle((current) => current?.id === id ? null : current);
      toast.success('Vehicle deleted.');
    } catch (err) {
      toast.error('Failed to delete vehicle.');
    }
  };

  const handleSuccess = (response) => {
    if (response) {
      if (editingVehicle) {
        // Update existing vehicle
        setVehicles((prev) =>
          prev.map((v) => (v.id === response.id ? response : v))
        );
      } else {
        // Add new vehicle
        setVehicles((prev) => [...prev, response]);
      }
    }
    setEditingVehicle(null);
    setIsAdding(false);
  };

  const handleCancel = () => {
    setEditingVehicle(null);
    setIsAdding(false);
  };

  const columns = ['Vehicle Number', 'Type', 'Actions'];

  const renderRow = (vehicle) => (
    <tr key={vehicle.id}>
      <td>{vehicle.vehicleNo}</td>
      <td>
        {vehicle.type === 'two-wheeler' ? (
          <FaMotorcycle size={24} color="#007bff" title="Two-Wheeler" />
        ) : (
          <FaCar size={24} color="#28a745" title="Four-Wheeler" />
        )}
      </td>
      <td>
        <IconButton
          icon={<FaEdit />}
          tooltip="Edit"
          variant="warning"
          onClick={() => {
            setIsAdding(false); // Exit add mode if active
            setEditingVehicle(vehicle);
          }}
          className="me-2"
        />
        <IconButton
          icon={<FaFileAlt />}
          tooltip="RC document"
          variant="primary"
          onClick={() => setDocumentVehicle((current) => current?.id === vehicle.id ? null : vehicle)}
          className="me-2"
        />
        <IconButton
          icon={<FaTrash />}
          tooltip="Delete"
          variant="danger"
          onClick={() => handleDelete(vehicle.id)}
        />
      </td>
    </tr>
  );

  return (
    <section className="vehicle-surface animate__animated animate__fadeIn">
      <div className="card-body">
        <div className="vehicle-section-header">
          <div>
            <span className="vehicle-panel-kicker">Parking profile</span>
            <h3 className="vehicle-section-title">My vehicles</h3>
            <p className="vehicle-section-copy">{vehicles.length} registered {vehicles.length === 1 ? 'vehicle' : 'vehicles'}</p>
          </div>
          <Button
            variant="primary"
            onClick={() => {
              setEditingVehicle(null); // Exit edit mode if active
              setIsAdding(true);
            }}
            disabled={isAdding || editingVehicle !== null}
          >
            <FaPlus /> Add vehicle
          </Button>
        </div>
        {(isAdding || editingVehicle !== null) && (
          <VehicleForm
            vehicle={editingVehicle}
            onSuccess={handleSuccess}
            onCancel={handleCancel}
          />
        )}
        {documentVehicle && (
          <VehicleDocuments vehicle={documentVehicle} onClose={() => setDocumentVehicle(null)} />
        )}
        <Table columns={columns} data={vehicles} renderRow={renderRow} />
      </div>
    </section>
  );
}

VehicleList.propTypes = {
  vehicles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      vehicleNo: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
    })
  ).isRequired,
  setVehicles: PropTypes.func.isRequired,
};

export default VehicleList;
